/**
 * Quiz attempts review page.
 * Displays persisted quiz runs for creators/admins, including filters, exports, and per-attempt detail.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Spin,
  Table,
  Tag,
  message,
  Popconfirm,
  Tooltip,
  Modal,
  DatePicker,
  Input,
  Space,
  Avatar,
  Descriptions,
  Collapse,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  BarChartOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EditOutlined,
  UserOutlined,
  CheckCircleFilled,
  CheckOutlined,
  CloseCircleFilled,
  ClockCircleOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import PagesWrapper from '../../PagesWrapper';
import quizCollectionsService from '../../services/quizCollectionsService';
import { formatDurationFromMilliseconds, formatDurationFromSeconds } from '../../utils/timeUtils';
import styles from './QuizAttempts.module.css';

const { RangePicker } = DatePicker;
const { Search } = Input;

/**
 * Format an ISO date string for local display.
 *
 * @param {string|null|undefined} dateStr - Date value returned by the API.
 * @returns {string} Localized timestamp or "-" when absent.
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
};

/**
 * Review page for recorded quiz attempts.
 * Provides table filters, CSV export, deletion controls, and a detailed modal per attempt.
 *
 * @returns {JSX.Element} Attempts management page.
 */
const QuizAttempts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [collectionName, setCollectionName] = useState('');
  const [attempts, setAttempts] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [attemptDetail, setAttemptDetail] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const buildParams = useCallback((page = 1, overrides = {}) => {
    const effectiveSearchText = overrides.searchText ?? searchText;
    const effectiveDateRange = overrides.dateRange ?? dateRange;

    return {
      page,
      pageSize: pagination.pageSize,
      search: effectiveSearchText || undefined,
      from: effectiveDateRange?.[0]?.toISOString() || undefined,
      to: effectiveDateRange?.[1]?.toISOString() || undefined,
    };
  }, [pagination.pageSize, searchText, dateRange]);

  const fetchAttempts = useCallback(async (page = 1, overrides = {}) => {
    try {
      setLoading(true);
      const data = await quizCollectionsService.getQuizAttempts(id, buildParams(page, overrides));
      setAttempts(data.items || []);
      setCollectionName(data.collectionName || '');
      setPagination(prev => ({
        ...prev,
        current: data.page,
        total: data.totalCount,
      }));
    } catch (error) {
      message.error(t('quiz.attempts.fetchError', 'Failed to load attempts'));
      navigate('/quiz-collections');
    } finally {
      setLoading(false);
    }
  }, [id, buildParams, navigate, t]);

  const fetchAttemptDetail = useCallback(async (attemptId) => {
    try {
      setLoadingDetail(true);
      const data = await quizCollectionsService.getQuizAttemptDetail(id, attemptId);
      setAttemptDetail(data);
    } catch (error) {
      message.error(t('quiz.attempts.detailError', 'Failed to load attempt details'));
    } finally {
      setLoadingDetail(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchAttempts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleTableChange = (pag) => {
    fetchAttempts(pag.current);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    fetchAttempts(1, { searchText: value });
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    fetchAttempts(1, { dateRange: dates });
  };

  const handleRowClick = (record) => {
    setModalOpen(true);
    fetchAttemptDetail(record.id);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setAttemptDetail(null);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await quizCollectionsService.downloadQuizAttemptsExport(id);
      message.success(t('quiz.attempts.exportSuccess', 'Export downloaded'));
    } catch (error) {
      message.error(t('quiz.attempts.exportError', 'Failed to export'));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAttempt = async (attemptId, e) => {
    e?.stopPropagation();
    try {
      setDeleting(true);
      await quizCollectionsService.deleteQuizAttempt(id, attemptId);
      message.success(t('quiz.attempts.deleted', 'Attempt deleted'));
      setSelectedRowKeys(prev => prev.filter(k => k !== attemptId));
      fetchAttempts(pagination.current);
    } catch (error) {
      message.error(t('quiz.attempts.deleteError', 'Failed to delete attempt'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;
    try {
      setDeleting(true);
      const result = await quizCollectionsService.bulkDeleteQuizAttempts(id, selectedRowKeys);
      message.success(
        t('quiz.attempts.bulkDeleted', '{{count}} attempts deleted', { count: result.deleted })
      );
      setSelectedRowKeys([]);
      fetchAttempts(1);
    } catch (error) {
      message.error(t('quiz.attempts.deleteError', 'Failed to delete attempts'));
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      title: t('quiz.attempts.player', 'Player'),
      key: 'player',
      render: (_, record) => (
        <div className={styles.playerCell}>
          <Avatar
            size={28}
            icon={<UserOutlined />}
            className={record.userId ? styles.userAvatar : styles.anonAvatar}
          />
          <div className={styles.playerInfo}>
            <span className={styles.playerName}>
              {record.userDisplayName || t('quiz.anonymous', 'Anonymous')}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: t('quiz.score', 'Score'),
      key: 'score',
      width: 100,
      render: (_, record) => (
        <Tag color={record.scorePercent >= 70 ? 'green' : record.scorePercent >= 40 ? 'orange' : 'red'}>
          {record.scorePercent != null ? `${record.scorePercent.toFixed(0)}%` : '-'}
        </Tag>
      ),
    },
    {
      title: t('quiz.attempts.answers', 'Correct'),
      key: 'correct',
      width: 100,
      render: (_, record) => (
        <span>
          <span style={{ color: '#52c41a' }}>{record.correctCount}</span>
          <span style={{ color: 'rgba(0,0,0,0.3)' }}>/</span>
          {record.playedQuestionCount}
        </span>
      ),
    },
    {
      title: t('quiz.duration', 'Duration'),
      dataIndex: 'totalDurationSeconds',
      key: 'duration',
      width: 100,
      render: (sec) => (
        <span className={styles.durationCell}>
          <ClockCircleOutlined className={styles.durationIcon} />
          {formatDurationFromSeconds(sec)}
        </span>
      ),
    },
    {
      title: t('surveys.completedAt', 'Completed At'),
      dataIndex: 'completedAt',
      key: 'completedAt',
      width: 170,
      render: (text) => formatDate(text),
    },
    {
      title: t('common.actions', 'Actions'),
      key: 'actions',
      width: 70,
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title={t('quiz.attempts.deleteConfirm', 'Delete this attempt?')}
          onConfirm={(e) => handleDeleteAttempt(record.id, e)}
          onCancel={(e) => e?.stopPropagation()}
          okText={t('common.delete', 'Delete')}
          cancelText={t('common.cancel', 'Cancel')}
          okButtonProps={{ danger: true }}
        >
          <Tooltip title={t('common.delete', 'Delete')}>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={(e) => e.stopPropagation()}
              loading={deleting}
              size="small"
            />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const renderDetailModal = () => {
    if (!attemptDetail) return null;
    const { correctCount, playedQuestionCount, scorePercent, totalDurationSeconds, completedAt, answers } = attemptDetail;

    const { userDisplayName, isAnonymous, userEmail, topicFilterName, startedAt } = attemptDetail;

    return (
      <>
        <Descriptions
          size="small"
          column={2}
          bordered
          className={styles.detailDescriptions}
        >
          <Descriptions.Item
            label={
              <span>
                <UserOutlined style={{ marginRight: 6 }} />
                {t('quiz.attempts.player', 'Player')}
              </span>
            }
            span={2}
          >
            <Space>
              <Avatar
                size={22}
                icon={<UserOutlined />}
                className={isAnonymous ? styles.anonAvatar : styles.userAvatar}
              />
              <span style={{ fontWeight: 500 }}>
                {userDisplayName || t('quiz.anonymous', 'Anonymous')}
              </span>
              {userEmail && (
                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                  {userEmail}
                </span>
              )}
              {isAnonymous && (
                <Tag color="default" style={{ fontSize: 11 }}>
                  {t('quiz.attempts.anonymous', 'Guest')}
                </Tag>
              )}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t('quiz.score', 'Score')}>
            <Tag color={scorePercent >= 70 ? 'green' : scorePercent >= 40 ? 'orange' : 'red'} style={{ fontWeight: 600 }}>
              {scorePercent != null ? `${scorePercent.toFixed(1)}%` : '-'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('quiz.attempts.answers', 'Correct')}>
            <span style={{ color: '#52c41a', fontWeight: 600 }}>{correctCount}</span>
            <span style={{ color: 'rgba(0,0,0,0.3)', margin: '0 2px' }}>/</span>
            <span>{playedQuestionCount}</span>
          </Descriptions.Item>

          <Descriptions.Item label={t('quiz.duration', 'Duration')}>
            <ClockCircleOutlined style={{ marginRight: 5, color: 'rgba(0,0,0,0.45)' }} />
            {formatDurationFromSeconds(totalDurationSeconds)}
          </Descriptions.Item>
          <Descriptions.Item label={t('surveys.completedAt', 'Completed')}>
            {formatDate(completedAt)}
          </Descriptions.Item>

         
          {topicFilterName && (
            <Descriptions.Item label={t('quiz.topicFilter', 'Topic')}>
              <Tag color="blue">{topicFilterName}</Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {answers && answers.length > 0 && (
          <Collapse
            size="small"
            className={styles.answersCollapse}
            items={answers.map((ans, i) => ({
              key: ans.id,
              label: (
                <span
                  className={styles.collapseLabel}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    {ans.isCorrect
                      ? <CheckCircleFilled style={{ color: '#52c41a', marginRight: 8 }} />
                      : <CloseCircleFilled style={{ color: '#ff4d4f', marginRight: 8 }} />
                    }
                    <span className={styles.collapseQuestionCode}>
                      {ans.questionCode || `Q${i + 1}`}
                    </span>
                    {ans.questionBody && (
                      <span className={styles.collapseQuestionBody}>
                        {ans.questionBody.replace(/<[^>]*>/g, '').slice(0, 80)}
                        {ans.questionBody.replace(/<[^>]*>/g, '').length > 80 ? '…' : ''}
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      flexShrink: 0,
                    }}
                  >
                    {ans.clientDurationMs != null && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '3px 10px',
                          borderRadius: 999,
                          background: '#f5f5f5',
                          border: '1px solid #e5e7eb',
                          color: '#6b7280',
                          fontSize: 12,
                          fontWeight: 500,
                        }}
                      >
                        <ClockCircleOutlined />
                        <span>{formatDurationFromMilliseconds(ans.clientDurationMs)}</span>
                      </span>
                    )}
                    {!ans.questionId && (
                      <Tag color="red" style={{ marginLeft: 0, fontSize: 10 }}>
                        {t('quiz.statistics.deleted', 'Deleted')}
                      </Tag>
                    )}
                  </span>
                </span>
              ),
              children: (
                <div className={styles.choicesList}>
                  {(ans.allChoices || []).map((c) => {
                    const selected = c.wasSelected;
                    const correct = c.isCorrect;
                    const selectedCorrectButWrongAnswer = !ans.isCorrect && selected && correct;
                    let cls = styles.choiceRow;
                    if (correct) cls += ` ${styles.choiceCorrect}`;
                    if (selected && !correct) cls += ` ${styles.choiceWrong}`;
                    return (
                      <div key={c.choiceId ?? c.text} className={cls}>
                        <span className={styles.choiceIndicator}>
                          {correct && <CheckCircleFilled style={{ color: '#52c41a' }} />}
                          {selected && !correct && <CloseCircleFilled style={{ color: '#ff4d4f' }} />}
                        </span>
                        <span>{c.text || t('quiz.statistics.imageChoice', '(Image)')}</span>
                        {selectedCorrectButWrongAnswer && (
                          <span
                            className={styles.choicePartialCorrectMarker}
                            title={t('quiz.attempts.selectedCorrectChoice', 'Selected correct choice')}
                          >
                            <CheckOutlined />
                          </span>
                        )}
                        {c.isFromSnapshot && (
                          <Tag color="default" style={{ marginLeft: 8, fontSize: 10 }}>
                            {t('quiz.attempts.snapshot', 'Snapshot')}
                          </Tag>
                        )}
                      </div>
                    );
                  })}
                </div>
              ),
            }))}
          />
        )}
      </>
    );
  };

  if (loading) {
    return (
      <PagesWrapper>
        <div className={styles.attemptsContainer}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
            <Spin size="large" />
          </div>
        </div>
      </PagesWrapper>
    );
  }

  return (
    <PagesWrapper>
      <div className={styles.attemptsContainer}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/quiz-collections')}
            >
              {t('common.back', 'Back')}
            </Button>
            <h1>
              {collectionName || '…'} - {t('quiz.attempts.title', 'Attempts')}
            </h1>
          </div>
          <div className={styles.headerActions}>
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/quiz-collections/${id}`)}
            >
              {t('quiz.edit', 'Edit')}
            </Button>
            <Button
              icon={<BarChartOutlined />}
              onClick={() => navigate(`/quiz-collections/${id}/statistics`)}
            >
              {t('quiz.statistics.title', 'Statistics')}
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
            >
              {t('common.export', 'Export CSV')}
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => fetchAttempts(1)}>
              {t('common.refresh', 'Refresh')}
            </Button>
          </div>
        </div>

        <div className={styles.toolbar}>
          <Space wrap>
            <Search
              placeholder={t('quiz.attempts.searchPlaceholder', 'Search by name or email…')}
              onSearch={handleSearch}
              allowClear
              style={{ width: 260 }}
            />
            <RangePicker onChange={handleDateChange} />
          </Space>
          {selectedRowKeys.length > 0 && (
            <Popconfirm
              title={t('quiz.attempts.bulkDeleteConfirm', 'Delete {{count}} selected attempts?', { count: selectedRowKeys.length })}
              onConfirm={handleBulkDelete}
              okText={t('common.delete', 'Delete')}
              cancelText={t('common.cancel', 'Cancel')}
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} loading={deleting}>
                {t('quiz.attempts.deleteSelected', 'Delete Selected ({{count}})', { count: selectedRowKeys.length })}
              </Button>
            </Popconfirm>
          )}
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={attempts}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
            showTotal: (total) => t('common.totalItems', '{{total}} items', { total }),
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
            style: { cursor: 'pointer' },
          })}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
        />

        <Modal
          open={modalOpen}
          onCancel={handleCloseModal}
          title={
            <span>
              <GlobalOutlined style={{ marginRight: 8 }} />
              {t('quiz.attempts.detailTitle', 'Attempt Detail')}
            </span>
          }
          footer={[
            <Button key="close" onClick={handleCloseModal}>
              {t('common.close', 'Close')}
            </Button>,
          ]}
          width={700}
        >
          {loadingDetail ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin size="large" />
            </div>
          ) : (
            renderDetailModal()
          )}
        </Modal>
      </div>
    </PagesWrapper>
  );
};

export default QuizAttempts;
